import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MenuBar from "../MenuBar";
import { apiFetch } from "../api";
import { useI18n } from "../i18n/I18nContext";

const THEMES = {
  dark: {
    page: { bg: "#0a0a0a", color: "#fff" },
    card: { bg: "rgba(255,255,255,0.03)", border: "rgba(255,255,255,0.08)" },
    cardHeader: { bg: "rgba(230,255,62,0.07)", border: "rgba(230,255,62,0.15)", color: "#e6ff3e" },
    th: { bg: "rgba(255,255,255,0.06)", border: "rgba(255,255,255,0.08)", color: "#94a3b8" },
    td: { border: "rgba(255,255,255,0.05)", color: "#e2e8f0" },
    accent: "#e6ff3e",
    accentText: "#000",
    toggleBtn: { bg: "rgba(255,255,255,0.06)", border: "rgba(255,255,255,0.12)", color: "#fff" },
  },
  light: {
    page: { bg: "#f1f5f9", color: "#0f172a" },
    card: { bg: "#fff", border: "#e2e8f0" },
    cardHeader: { bg: "#f8fafc", border: "#e2e8f0", color: "#475569" },
    th: { bg: "#f1f5f9", border: "#e2e8f0", color: "#64748b" },
    td: { border: "#f1f5f9", color: "#334155" },
    accent: "#1d4ed8",
    accentText: "#fff",
    toggleBtn: { bg: "#fff", border: "#e2e8f0", color: "#334155" },
  },
};

function applyFilter(arr, key, value) {
  if (!key || String(value).trim() === "") return arr;
  const needle = String(value).toLowerCase();
  return arr.filter((r) => r[key] != null && String(r[key]).toLowerCase().includes(needle));
}

function normalizeListResponse(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.items)) return data.items;
  return [];
}

function asFrNumber(val) {
  if (val == null || val === "") return "";
  const n = Number(val);
  return Number.isFinite(n) ? n.toLocaleString("fr-FR", { maximumFractionDigits: 4 }) : String(val);
}

function mapRowsFromApi(data) {
  return normalizeListResponse(data).map((it) => ({
    index: it.index,
    DateControle: it.DateControle,
    PhaseJournee: it.PhaseJournee,
    Operator: it.Operator,
    TypeResine: it.TypeResine,
    ViscositeMesuree: it.ViscositeMesuree,
    ExtraitSecPct: it.ExtraitSecPct,
    TemperaturePicExotherm: it.TemperaturePicExotherm,
    raw: it,
  }));
}

const PAGE_SIZE = 20;

export default function ConsultationControleResine() {
  const { ui } = useI18n();
  const navigate = useNavigate();

  const [mode, setMode] = useState(() => localStorage.getItem("qsf-theme") || "dark");
  const T = THEMES[mode];

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filterKey, setFilterKey] = useState("Operator");
  const [filterValue, setFilterValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiFetch("/api/controle-resine/list");
        if (!r.ok) throw new Error("Erreur serveur");
        const data = await r.json();
        if (!cancelled) setRows(mapRowsFromApi(data));
      } catch (e) {
        if (!cancelled) setErr(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const view = useMemo(() => {
    const filtered = applyFilter([...rows], filterKey, filterValue);
    return filtered.sort((a, b) => Number(b.index) - Number(a.index));
  }, [rows, filterKey, filterValue]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterKey, filterValue]);

  const totalPages = Math.max(1, Math.ceil(view.length / PAGE_SIZE));
  const pageSafe = Math.min(currentPage, totalPages);
  const paginated = view.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);

  const handleEdit = (raw) => {
    navigate("/qualite/controleStack/controle-resine", { state: { item: raw, edit: true } });
  };

  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState("");

  const exportExcel = async () => {
    try {
      const r = await apiFetch("/api/controle-resine/export-excel");
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.message || "Export impossible");
      }
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `controle_resine_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
    } catch (e) {
      setErr(e.message || "Erreur export Excel");
    }
  };

  const importExcel = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    setImportMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await apiFetch("/api/controle-resine/import-excel", { method: "POST", body: fd });
      const j = await r.json();
      if (j.status !== "success") throw new Error(j.message || "Import échoué");
      setImportMsg(j.message);
      const lr = await apiFetch("/api/controle-resine/list");
      const data = await lr.json();
      setRows(mapRowsFromApi(data));
      setCurrentPage(1);
    } catch (ex) {
      setImportMsg(ex.message || "Erreur import");
    } finally {
      setImporting(false);
    }
  };

  const s = {
    page: { minHeight: "100vh", background: T.page.bg, color: T.page.color, fontFamily: "system-ui,sans-serif" },
    wrap: { padding: "20px 16px 48px" },
    topBar: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 },
    title: { fontWeight: 900, fontSize: 18, textTransform: "uppercase" },
    card: { background: T.card.bg, border: `1px solid ${T.card.border}`, borderRadius: 16, overflow: "hidden" },
    cardHeader: { padding: "12px 18px", background: T.cardHeader.bg, borderBottom: `1px solid ${T.cardHeader.border}`, fontWeight: 800, fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: T.cardHeader.color },
    toolbar: { display: "flex", flexWrap: "wrap", gap: 10, padding: 14, alignItems: "center" },
    th: { background: T.th.bg, color: T.th.color, fontSize: 10, fontWeight: 700, textTransform: "uppercase", padding: "10px 8px", borderBottom: `1px solid ${T.th.border}`, whiteSpace: "nowrap", textAlign: "center" },
    td: { padding: "10px 8px", fontSize: 12, borderBottom: `1px solid ${T.td.border}`, color: T.td.color, whiteSpace: "nowrap", textAlign: "center", verticalAlign: "middle" },
    btn: { background: T.accent, color: T.accentText, border: "none", borderRadius: 8, padding: "8px 14px", fontWeight: 800, cursor: "pointer", fontSize: 12 },
    toggleBtn: { background: T.toggleBtn.bg, border: `1px solid ${T.toggleBtn.border}`, color: T.toggleBtn.color, borderRadius: 10, padding: "8px 14px", cursor: "pointer" },
    counter: { padding: "10px 16px", fontSize: 12, fontWeight: 600, opacity: 0.85, borderBottom: `1px solid ${T.td.border}`, textAlign: "center" },
    pagination: { display: "flex", justifyContent: "center", alignItems: "center", gap: 8, padding: "16px", flexWrap: "wrap", borderTop: `1px solid ${T.td.border}` },
    pageBtn: { minWidth: 36, height: 36, padding: "0 10px", borderRadius: 8, border: `1px solid ${T.td.border}`, background: T.card.bg, color: T.page.color, fontWeight: 700, fontSize: 12, cursor: "pointer" },
    pageBtnActive: { background: T.accent, color: T.accentText, border: "none" },
    pageInfo: { fontSize: 12, fontWeight: 600, padding: "0 8px", opacity: 0.9 },
  };

  return (
    <div style={s.page}>
      <MenuBar active="qualite" />
      <div style={s.wrap}>
        <div style={s.topBar}>
          <span style={s.title}>Suivi contrôle résine</span>
          <button type="button" style={s.toggleBtn} onClick={() => setMode((m) => (m === "dark" ? "light" : "dark"))}>
            {mode === "dark" ? "☀️" : "🌙"}
          </button>
        </div>

        <div style={s.card}>
          <div style={s.cardHeader}>Mesures enregistrées</div>
          <div style={s.toolbar}>
            <select value={filterKey} onChange={(e) => setFilterKey(e.target.value)} style={{ height: 34, borderRadius: 8, padding: "0 8px" }}>
              <option value="Operator">Opérateur</option>
              <option value="PhaseJournee">Phase</option>
              <option value="TypeResine">Type résine</option>
              <option value="DateControle">Date contrôle</option>
            </select>
            <input value={filterValue} onChange={(e) => setFilterValue(e.target.value)} placeholder={ui("Filtrer…")} style={{ height: 34, borderRadius: 8, padding: "0 10px", width: 140 }} />
            <button type="button" style={s.btn} onClick={exportExcel}>Export Excel (KOSTER)</button>
            <label style={{ ...s.btn, display: "inline-flex", alignItems: "center", cursor: "pointer" }}>
              {importing ? "Import…" : "Import Excel"}
              <input type="file" accept=".xlsx,.xls" style={{ display: "none" }} onChange={importExcel} disabled={importing} />
            </label>
          </div>
          {importMsg && <p style={{ padding: "0 14px 10px", fontSize: 13, textAlign: "center" }}>{importMsg}</p>}

          {loading && <p style={{ padding: 16, textAlign: "center" }}>Chargement…</p>}
          {err && <p style={{ padding: 16, color: "#f87171", textAlign: "center" }}>{err}</p>}

          {!loading && !err && (
            <>
              <div style={s.counter}>
                {view.length} résultat{view.length > 1 ? "s" : ""} · page {pageSafe}/{totalPages}
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {["Index", "Date", "Phase", "Opérateur", "Type", "Viscosité", "Extrait sec %", "Pic exo °C", "Actions"].map((h) => (
                        <th key={h} style={s.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {view.length === 0 ? (
                      <tr><td colSpan={9} style={s.td}>Aucune mesure</td></tr>
                    ) : (
                      paginated.map((r) => (
                        <tr key={r.index}>
                          <td style={s.td}>{r.index ?? "—"}</td>
                          <td style={s.td}>{r.DateControle ?? "—"}</td>
                          <td style={s.td}>{r.PhaseJournee ?? "—"}</td>
                          <td style={s.td}>{r.Operator ?? "—"}</td>
                          <td style={s.td}>{r.TypeResine ?? "—"}</td>
                          <td style={s.td}>{asFrNumber(r.ViscositeMesuree) || "—"}</td>
                          <td style={s.td}>{asFrNumber(r.ExtraitSecPct) || "—"}</td>
                          <td style={s.td}>{asFrNumber(r.TemperaturePicExotherm) || "—"}</td>
                          <td style={s.td}>
                            <button type="button" style={s.btn} onClick={() => handleEdit(r.raw || r)}>Modifier</button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {view.length > 0 && (
                <div style={s.pagination}>
                  <button
                    type="button"
                    style={{ ...s.pageBtn, opacity: pageSafe === 1 ? 0.35 : 1 }}
                    disabled={pageSafe === 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  >
                    ← Préc.
                  </button>
                  <span style={s.pageInfo}>
                    {(pageSafe - 1) * PAGE_SIZE + 1}–{Math.min(pageSafe * PAGE_SIZE, view.length)} / {view.length}
                  </span>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((p) => p === 1 || p === totalPages || Math.abs(p - pageSafe) <= 2)
                    .reduce((acc, p, idx, arr) => {
                      if (idx > 0 && p - arr[idx - 1] > 1) acc.push("…");
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((p, idx) =>
                      p === "…" ? (
                        <span key={`dots-${idx}`} style={s.pageInfo}>…</span>
                      ) : (
                        <button
                          key={p}
                          type="button"
                          style={{ ...s.pageBtn, ...(p === pageSafe ? s.pageBtnActive : {}) }}
                          onClick={() => setCurrentPage(p)}
                        >
                          {p}
                        </button>
                      )
                    )}
                  <button
                    type="button"
                    style={{ ...s.pageBtn, opacity: pageSafe === totalPages ? 0.35 : 1 }}
                    disabled={pageSafe === totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Suiv. →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
